import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useFormContext } from '../context/FormContext';
import { getSessionId, getSessionIdSync } from '../lib/supabase';
import { createLead, updateLeadPaymentMethod, updateLeadData } from '../services/leadsService';
import { saveFormStep } from '../services/formSubmissionService';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CTAButton from '../components/CTAButton';
import GermanRegistryInfo from '../components/GermanRegistryInfo';
import { logger } from '../lib/utils';
import { toast } from 'sonner';

const Payment: React.FC = () => {
  const navigate = useNavigate();
  const { formData, updateFormData, isRestoringData } = useFormContext();
  const [errors, setErrors] = useState<{
    paymentMethod?: string;
    terms?: string;
    marketing?: string;
  }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCreatingLead, setIsCreatingLead] = useState(false);

  // Use a ref to track if we've already processed lead creation for this mount
  const leadCreationProcessedRef = useRef(false);

  // Create a new lead when payment page is reached
  useEffect(() => {
    const createLeadForPayment = async () => {
      // Wait for data restoration to complete before checking
      if (isRestoringData) {
        logger.log('Waiting for data restoration to complete...');
        return;
      }

      // Prevent duplicate execution
      if (leadCreationProcessedRef.current) {
        logger.log('Lead creation already processed for this page visit');
        return;
      }

      // Mark as processed immediately to prevent race conditions
      leadCreationProcessedRef.current = true;

      // Only create if we have email and don't already have an order number
      if (!formData.email) {
        logger.error('No email found - cannot create lead');
        toast.error('Keine E-Mail-Adresse gefunden. Bitte füllen Sie das Formular erneut aus.');
        navigate('/email');
        return;
      }

      // Check if order number already exists (e.g., returning from Stripe or user went back and changed selections)
      if (formData.orderNumber) {
        logger.log('Order number already exists:', formData.orderNumber, '- updating lead with latest form data');

        // Update the existing lead with the latest form data (including email in case it changed)
        setIsCreatingLead(true);
        try {
          // Use async version to ensure session is fully initialized in database
          const sessionId = await getSessionId();

          const result = await updateLeadData(sessionId, {
            email: formData.email, // Include email in case it was changed
            knownInfo: formData.knownInfo,
            street: formData.street,
            houseNumber: formData.houseNumber,
            birthdate: formData.birthdate,
            gender: formData.gender,
            firstName: formData.firstName,
            lastName: formData.lastName,
            country: formData.country,
            city: formData.city,
            postalCode: formData.postalCode,
            agreesToTerms: formData.agreesToTerms,
            agreesToNoMarketing: formData.agreesToNoMarketing,
          });

          if (result.success) {
            logger.log('Lead updated successfully with latest form data');
          } else {
            logger.warn('Failed to update lead:', result.error);
            // Don't show error to user - not critical since lead already exists
          }
        } catch (error) {
          logger.error('Error updating lead:', error);
        } finally {
          setIsCreatingLead(false);
        }

        return;
      }

      setIsCreatingLead(true);

      try {
        // Use async version to ensure session is fully initialized in database
        const sessionId = await getSessionId();
        logger.log('Creating NEW lead for payment page with session:', sessionId);

        const result = await createLead(sessionId, formData.email, {
          gender: formData.gender,
          firstName: formData.firstName,
          lastName: formData.lastName,
          country: formData.country,
          city: formData.city,
          postalCode: formData.postalCode,
          street: formData.street,
          houseNumber: formData.houseNumber,
          birthdate: formData.birthdate,
          knownInfo: formData.knownInfo,
          agreesToTerms: formData.agreesToTerms,
          agreesToNoMarketing: formData.agreesToNoMarketing,
        });

        if (result.success && result.order_number) {
          logger.log('New lead created with order number:', result.order_number);
          updateFormData('orderNumber', result.order_number);
          toast.success(`Bestellnummer ${result.order_number} erstellt`);
        } else {
          logger.error('Failed to create lead:', result.error);
          toast.error('Fehler beim Erstellen der Bestellung. Bitte versuchen Sie es erneut.');
        }
      } catch (error) {
        logger.error('Error creating lead on payment page:', error);
        toast.error('Fehler beim Erstellen der Bestellung. Bitte versuchen Sie es erneut.');
      } finally {
        setIsCreatingLead(false);
      }
    };

    createLeadForPayment();
  }, [isRestoringData]); // Only depend on isRestoringData to trigger once when ready

  // Reset the ref when navigating away and back
  useEffect(() => {
    return () => {
      leadCreationProcessedRef.current = false;
    };
  }, []);

  // Reset isSubmitting when returning from Stripe via browser back button (bfcache restore)
  useEffect(() => {
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        setIsSubmitting(false);
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  // Auto-save payment method, terms, and marketing consent when they change
  useEffect(() => {
    const savePaymentData = async () => {
      // Don't save if still restoring data or if no order number yet or if lead is being created
      if (isRestoringData || !formData.orderNumber || isCreatingLead) {
        return;
      }

      // Add a small delay to ensure lead creation is fully committed
      await new Promise(resolve => setTimeout(resolve, 500));

      try {
        // Use async version to ensure session exists
        const sessionId = await getSessionId();

        // Save to form_submissions table (payment step)
        if (formData.paymentMethod || formData.agreesToTerms || formData.agreesToNoMarketing) {
          logger.log('Auto-saving payment data:', {
            paymentMethod: formData.paymentMethod,
            agreesToTerms: formData.agreesToTerms,
            agreesToNoMarketing: formData.agreesToNoMarketing,
          });

          await saveFormStep(sessionId, 'payment', {
            email: formData.email,
            paymentMethod: formData.paymentMethod,
            agreesToTerms: formData.agreesToTerms,
            agreesToNoMarketing: formData.agreesToNoMarketing,
          }, false);
        }

        // Save payment method to leads table
        if (formData.paymentMethod) {
          logger.log('Auto-saving payment method to leads table:', formData.paymentMethod);
          await updateLeadPaymentMethod(sessionId, formData.paymentMethod, false);
        }
      } catch (error) {
        logger.error('Error auto-saving payment data:', error);
        // Don't show error to user - this is a background save
      }
    };

    savePaymentData();
  }, [formData.paymentMethod, formData.agreesToTerms, formData.agreesToNoMarketing, formData.orderNumber, formData.email, isRestoringData, isCreatingLead]);

  const validateForm = () => {
    const newErrors: {
      paymentMethod?: string;
      terms?: string;
      marketing?: string;
    } = {};

    if (!formData.paymentMethod) {
      newErrors.paymentMethod = 'Bitte wählen Sie eine Zahlungsart';
    }

    if (!formData.agreesToTerms) {
      newErrors.terms = 'Bitte stimmen Sie den AGB und Datenschutzerklärung zu';
    }

    if (!formData.agreesToNoMarketing) {
      newErrors.marketing = 'Bitte bestätigen Sie, dass die Anfrage nicht zwecks Werbung gestellt wird';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Wait for lead creation to complete if it's still in progress
    if (isCreatingLead) {
      toast.info('Bitte warten Sie, bis die Bestellung erstellt wurde...');
      return;
    }

    // Check if we have an order number
    if (!formData.orderNumber) {
      logger.error('No order number available');
      toast.error('Bestellnummer fehlt. Bitte versuchen Sie es erneut.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Use async version to ensure session exists
      const sessionId = await getSessionId();

      // Save payment method to form_submissions (payment step) and leads table
      // Use await to ensure data is saved before redirecting to Stripe
      if (formData.paymentMethod) {
        logger.log('Saving payment method before Stripe redirect:', formData.paymentMethod);

        try {
          // Save to form_submissions table (payment step)
          await saveFormStep(sessionId, 'payment', {
            email: formData.email,
            paymentMethod: formData.paymentMethod,
            agreesToTerms: formData.agreesToTerms,
            agreesToNoMarketing: formData.agreesToNoMarketing,
          }, true);

          logger.log('Payment step saved successfully');
        } catch (error) {
          logger.error('Error saving payment step:', error);
          // Continue anyway - localStorage has the data
        }

        try {
          // Update payment method in leads table (using session ID)
          await updateLeadPaymentMethod(sessionId, formData.paymentMethod, false);
          logger.log('Payment method saved to leads table successfully');
        } catch (error) {
          logger.error('Error updating payment method in leads table:', error);
          // Continue anyway - localStorage has the data
        }

        try {
          // Update agrees_to_terms and agrees_to_no_marketing in leads table
          await updateLeadData(sessionId, {
            agreesToTerms: formData.agreesToTerms,
            agreesToNoMarketing: formData.agreesToNoMarketing,
          });
          logger.log('Agreement fields saved to leads table successfully');
        } catch (error) {
          logger.error('Error updating agreement fields in leads table:', error);
        }
      }

      // If bank transfer, redirect to bank transfer instructions page instead of Stripe
      if (formData.paymentMethod === 'bankueberweisung') {
        logger.log('Bank transfer selected - redirecting to instructions page');
        setIsSubmitting(false);
        navigate('/bankueberweisung');
        return;
      }

      // Construct the Stripe checkout URL
      // SANDBOX URL for testing
      const sandboxUrl = 'https://buy.stripe.com/test_14k15o1O4exU968144';

      // Live URL
      const liveUrl = 'https://buy.stripe.com/3cs8zh3IacJ552M6op';

      // Check if URL contains stripetestpayment=true parameter
      const urlParams = new URLSearchParams(window.location.search);

      // Also check localStorage for stored query string
      const storedQueryString = localStorage.getItem('adressfinder_url_query_string') || '';
      const storedParams = new URLSearchParams(storedQueryString);

      // Use sandbox URL if either current URL or stored URL has stripetestpayment=true
      const useTestPayment = urlParams.get('stripetestpayment') === 'true' || storedParams.get('stripetestpayment') === 'true';
      let checkoutUrl = useTestPayment ? sandboxUrl : liveUrl;

      logger.log(useTestPayment ? 'Using SANDBOX Stripe checkout' : 'Using LIVE Stripe checkout');

      // Add client_reference_id parameter with order number
      const clientReferenceId = `${formData.orderNumber}_${sessionId}`;
      logger.log('Redirecting to Stripe with client_reference_id:', clientReferenceId);
      checkoutUrl += `?client_reference_id=${encodeURIComponent(clientReferenceId)}`;

      // Add prefilled_email parameter if available
      if (formData.email) {
        checkoutUrl += `&prefilled_email=${encodeURIComponent(formData.email)}`;
      }

      // Add success_url parameter to redirect back to our success page
      const domain = window.location.origin;
      const successUrl = `${domain}/success?session_id={CHECKOUT_SESSION_ID}&order_number=${encodeURIComponent(formData.orderNumber)}`;
      checkoutUrl += `&success_url=${encodeURIComponent(successUrl)}`;

      // Add cancel_url parameter to redirect back to payment page if user cancels
      const cancelUrl = `${domain}/payment`;
      checkoutUrl += `&cancel_url=${encodeURIComponent(cancelUrl)}`;

      // Redirect to Stripe checkout
      logger.log('Redirecting to:', checkoutUrl);
      window.location.href = checkoutUrl;

    } catch (error) {
      logger.error('Error during payment submission:', error);
      toast.error('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/email');
  };

  // Show loading state while data is being restored
  if (isRestoringData) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow container mx-auto px-4 py-8 max-w-5xl">
          <div className="form-container fade-in">
            <h2 className="text-2xl md:text-3xl text-center font-bold text-prussian-blue mb-8">
              Daten werden geladen...
            </h2>
            <div className="flex justify-center mb-8">
              <div className="relative w-20 h-20">
                <div className="absolute inset-0 border-8 border-gray-200 rounded-full"></div>
                <div
                  className="absolute inset-0 border-8 border-transparent rounded-full animate-spin"
                  style={{
                    borderTopColor: '#003a5d',
                    borderRightColor: '#003a5d'
                  }}
                ></div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-grow container mx-auto px-4 py-8 max-w-5xl">
        <div className="form-container fade-in">
          <h2 className="text-2xl md:text-3xl text-center font-bold text-prussian-blue mb-8">
            Ergebnis: Ihre gewünschte Adressanfrage ist möglich! ✅
          </h2>

          <div className="bg-gray-50 p-6 md:p-8 rounded-lg border border-gray-200 mb-8">
            <h3 className="text-xl md:text-2xl font-semibold text-prussian-blue mb-6">Angaben überprüfen:</h3>

            {/* Gesuchte Person */}
            <div className="mb-6 pb-6 border-b border-gray-200">
              <h4 className="text-base font-semibold text-gray-600 mb-2">Gesuchte Person:</h4>
              <p className="text-lg md:text-xl text-gray-900 font-medium">
                {formData.firstName || '[Vorname]'} {formData.lastName || '[Nachname]'} aus {formData.city || '[Ort]'} (Deutschland)
              </p>
            </div>

            {/* E-Mail Adresse */}
            <div className="mb-6 pb-6 border-b border-gray-200">
              <h4 className="text-base font-semibold text-gray-600 mb-2">Ihre E-Mail Adresse:</h4>
              <p className="text-lg md:text-xl text-gray-900 font-medium">{formData.email || '[E-Mail Adresse]'}</p>
              <button
                type="button"
                onClick={handleBack}
                className="mt-2 text-sm text-gray-600 hover:text-prussian-blue underline transition-colors"
              >
                Angaben ändern
              </button>
            </div>

            {/* Auskunftsart */}
            <div className="mb-6 pb-6 border-b border-gray-200">
              <h4 className="text-base font-semibold text-gray-600 mb-2">Auskunftsart</h4>
              <p className="text-base md:text-lg text-gray-900">
                <span className="font-semibold">Einfache Melderegisterauskunft</span><br />
                <span className="text-gray-700">Sie erhalten von der Meldebehörde Familienname, Vornamen und die derzeitige Anschrift zu der gesuchten Person.</span>
              </p>
            </div>

            {/* Gebühr & Ablauf */}
            <div>
              <h4 className="text-base font-semibold text-gray-600 mb-2">Gebühr & Ablauf</h4>
              <p className="text-base md:text-lg text-gray-900">
                Die Gebühr für die Melderegisterauskunft beträgt <span className="font-bold text-prussian-blue">34,80 EUR (inkl. MwSt.)</span><br /><br />
                <span className="text-gray-700">In der Regel liegt das Ergebnis innerhalb weniger Minuten vor. Sie erhalten die Melderegisterauskunft per E-Mail mitgeteilt.</span>
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <h3 className="text-lg md:text-xl font-semibold mb-3 text-left text-prussian-blue">Zahlungsart:</h3>
              <p className="text-base md:text-lg text-gray-600 mb-5 text-left">
                Bitte wählen Sie aus, wie Sie zahlen möchten:
              </p>

              <div className="space-y-3">
                <label className={`block p-4 border rounded-md cursor-pointer transition-all min-h-[60px] ${formData.paymentMethod === 'paypal' ? 'border-prussian-blue bg-blue-50' : 'hover:bg-gray-50'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="payment"
                        value="paypal"
                        checked={formData.paymentMethod === 'paypal'}
                        onChange={() => updateFormData('paymentMethod', 'paypal')}
                        className="h-5 w-5 text-prussian-blue focus:ring-prussian-blue flex-shrink-0"
                      />
                      <span className="ml-3 font-medium text-base md:text-lg">PayPal</span>
                    </div>
                    <img src="/payment_logos/paypal.svg" alt="PayPal" className="h-6 md:h-7" />
                  </div>
                </label>

                <label className={`block p-4 border rounded-md cursor-pointer transition-all min-h-[60px] ${formData.paymentMethod === 'klarna' ? 'border-prussian-blue bg-blue-50' : 'hover:bg-gray-50'}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start">
                      <input
                        type="radio"
                        name="payment"
                        value="klarna"
                        checked={formData.paymentMethod === 'klarna'}
                        onChange={() => updateFormData('paymentMethod', 'klarna')}
                        className="h-5 w-5 mt-0.5 text-prussian-blue focus:ring-prussian-blue flex-shrink-0"
                      />
                      <span className="ml-3 font-medium text-base md:text-lg">Klarna (Später zahlen & Ratenzahlung möglich)</span>
                    </div>
                    <img src="/payment_logos/klarna.svg" alt="Klarna" className="h-6 md:h-7 mt-0.5 flex-shrink-0" />
                  </div>
                </label>

                <label className={`block p-4 border rounded-md cursor-pointer transition-all min-h-[60px] ${formData.paymentMethod === 'kreditkarte' ? 'border-prussian-blue bg-blue-50' : 'hover:bg-gray-50'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="payment"
                        value="kreditkarte"
                        checked={formData.paymentMethod === 'kreditkarte'}
                        onChange={() => updateFormData('paymentMethod', 'kreditkarte')}
                        className="h-5 w-5 text-prussian-blue focus:ring-prussian-blue flex-shrink-0"
                      />
                      <span className="ml-3 font-medium text-base md:text-lg">Kreditkarte</span>
                    </div>
                    <img src="/payment_logos/credit-card-logos.png" alt="Kreditkarten" className="h-6 md:h-7" />
                  </div>
                </label>

                <label className={`block p-4 border rounded-md cursor-pointer transition-all min-h-[60px] ${formData.paymentMethod === 'applepay' ? 'border-prussian-blue bg-blue-50' : 'hover:bg-gray-50'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="payment"
                        value="applepay"
                        checked={formData.paymentMethod === 'applepay'}
                        onChange={() => updateFormData('paymentMethod', 'applepay')}
                        className="h-5 w-5 text-prussian-blue focus:ring-prussian-blue flex-shrink-0"
                      />
                      <span className="ml-3 font-medium text-base md:text-lg">Apple Pay</span>
                    </div>
                    <img src="/payment_logos/apple-pay.svg" alt="Apple Pay" className="h-6 md:h-7" />
                  </div>
                </label>

                <label className={`block p-4 border rounded-md cursor-pointer transition-all min-h-[60px] ${formData.paymentMethod === 'bankueberweisung' ? 'border-prussian-blue bg-blue-50' : 'hover:bg-gray-50'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="payment"
                        value="bankueberweisung"
                        checked={formData.paymentMethod === 'bankueberweisung'}
                        onChange={() => updateFormData('paymentMethod', 'bankueberweisung')}
                        className="h-5 w-5 text-prussian-blue focus:ring-prussian-blue flex-shrink-0"
                      />
                      <span className="ml-3 font-medium text-base md:text-lg">Vorkasse (Überweisung)</span>
                    </div>
                    <img src="/payment_logos/ueberweisung.svg" alt="Überweisung" className="h-6 md:h-7" />
                  </div>
                </label>
              </div>
              {errors.paymentMethod && <p className="mt-2 text-base text-red-600 text-center font-medium">{errors.paymentMethod}</p>}
            </div>

            <div className="my-8">
              <p
                className="text-center mb-2 text-base md:text-lg text-gray-700"
                dangerouslySetInnerHTML={{ __html: '⭐️⭐️⭐️⭐️⭐️ Das Urteil unserer Kunden: <strong>HERVORRAGEND 4.7 von 5 Sternen</strong>' }}
              />
              <p className="text-center mb-4 flex items-center justify-center gap-2 text-base md:text-lg text-gray-700">
                <span className="inline-block w-5 h-5">🔒</span>
                Ihre Daten werden streng vertraulich behandelt und nicht an Dritte weitergegeben.
              </p>
              <div className="flex justify-center items-center gap-6 mb-2 px-4">
                <div className="flex justify-center items-center gap-6 flex-nowrap">
                  <div className="flex overflow-hidden" style={{ width: '140px', height: '55px', minWidth: '140px' }}>
                    <img
                      src="/images/ssl-badge.svg"
                      alt="SSL Sichere Verbindung"
                      className="w-full h-full"
                      style={{
                        objectFit: 'contain',
                        maxWidth: '100%',
                        maxHeight: '100%'
                      }}
                    />
                  </div>

                  <div className="flex rounded-lg overflow-hidden" style={{ width: '140px', height: '55px', minWidth: '140px' }}>
                    <img
                      src="/images/dsgvo-badge.svg"
                      alt="DSGVO"
                      className="w-full h-full"
                      style={{
                        objectFit: 'contain',
                        maxWidth: '100%',
                        maxHeight: '100%'
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t pt-6 mt-6">
              <h3 className="text-lg md:text-xl font-semibold mb-5 text-left text-prussian-blue">Bitte bestätigen Sie:</h3>

              <div className="space-y-4">
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    checked={formData.agreesToTerms}
                    onChange={(e) => updateFormData('agreesToTerms', e.target.checked)}
                    className="h-5 w-5 min-h-[1.25rem] min-w-[1.25rem] mt-0.5 text-prussian-blue focus:ring-prussian-blue border-gray-300 rounded"
                  />
                  <span className="ml-3 text-base md:text-lg">
                    Ich stimme den <Link to="/agb" target="_blank" rel="noopener noreferrer" className="text-prussian-blue font-medium">AGB</Link>, der <Link to="/datenschutz" target="_blank" rel="noopener noreferrer" className="text-prussian-blue font-medium">Datenschutzerklärung</Link> und dem <Link to="/agb" target="_blank" rel="noopener noreferrer" className="text-prussian-blue font-medium">Widerrufsrecht</Link> von AdressFinder.org zu.
                  </span>
                </label>
                {errors.terms && <p className="mt-1 text-base text-red-600 font-medium">{errors.terms}</p>}

                <label className="flex items-start">
                  <input
                    type="checkbox"
                    checked={formData.agreesToNoMarketing}
                    onChange={(e) => updateFormData('agreesToNoMarketing', e.target.checked)}
                    className="h-5 w-5 min-h-[1.25rem] min-w-[1.25rem] mt-0.5 text-prussian-blue focus:ring-prussian-blue border-gray-300 rounded"
                  />
                  <span className="ml-3 text-base md:text-lg">
                    Ich versichere, die Anfrage nicht zwecks Werbung und/oder Adresshandel zu stellen.
                  </span>
                </label>
                {errors.marketing && <p className="mt-1 text-base text-red-600 font-medium">{errors.marketing}</p>}
              </div>
            </div>

            <div className="mt-10 flex justify-center">
              <CTAButton
                type="submit"
                text="Weiter zur Zahlung"
                arrow={true}
                disabled={isSubmitting}
                className={`w-full max-w-md text-base md:text-lg py-3 md:py-4 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
              />
            </div>
          </form>
        </div>

        <GermanRegistryInfo />
      </main>

      <Footer />
    </div>
  );
};

export default Payment;
